// src/components/layout/SideMenu.tsx
//
// The sidebar is rendered from a canonical route list owned by the
// frontend — matching the pattern used by other suite apps. It is NOT
// fetched from CRM's backend menu-template system. The route list is the
// source of truth for which links exist; visibility is filtered by:
//   1. CRM in-app role (Owner / Administrator / Coordinator / Agent /
//      Analyst / Viewer) via `MENU_KEY_ROLE_ACCESS`.
//   2. Central capability gates for telephony items (phone / sms), which
//      Central alone decides.
//
// SMS is never a standalone nav entry — it lives inside the Unibox/Messages
// page.

import { NavLink } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../store/store';
import { setSideMenuOpen } from '../../store/slices/layoutSlice';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentCrmRole } from '../../hooks/useCurrentCrmRole';
import type { CrmRoleKey } from '@levelup/crm-roles';
import { getMenuIcon } from './menuIcons';
import type { MenuKey } from '../../types/api';

// Access matrix: which CRM in-app role may see which menu key. Keys not
// listed here are visible to every role by default.
const MENU_KEY_ROLE_ACCESS: Partial<Record<MenuKey, ReadonlySet<CrmRoleKey>>> = {
  messages: new Set(['owner', 'administrator', 'coordinator', 'agent']),
  phone: new Set(['owner', 'administrator', 'coordinator', 'agent']),
  forms: new Set(['owner', 'administrator', 'coordinator']),
  channels: new Set(['owner', 'administrator', 'coordinator']),
  offerings: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  payments: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  businessProfile: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  portfolio: new Set(['owner', 'administrator', 'coordinator', 'analyst']),
  recruitment: new Set(['owner', 'administrator', 'coordinator']),
  marketplace: new Set(['owner', 'administrator', 'coordinator']),
  settings: new Set(['owner', 'administrator']),
};

// Canonical sidebar definition — frontend owns this list. Order here is
// the display order. `sms` is intentionally omitted (handled in Messages).
const SIDEBAR_ITEMS: ReadonlyArray<{
  menuKey: MenuKey;
  label: string;
  route: string;
}> = [
  { menuKey: 'home', label: 'Home', route: '/' },
  { menuKey: 'contacts', label: 'Contacts', route: '/contacts' },
  { menuKey: 'tasks', label: 'Tasks', route: '/tasks' },
  { menuKey: 'pipeline', label: 'Pipeline', route: '/pipeline' },
  // M1 — "Messages" means the Contact communications inbox (the Unibox).
  // It previously pointed at `/conversations`, which is the AI assistant
  // session list: a different bounded context that happens to contain the
  // word "message". That mismatch is the substance of the "Messages doesn't
  // work" report — the feature was never broken, the menu pointed elsewhere.
  // ONE communications destination. "Emails" used to follow this line pointing
  // at `/messages?medium=email` — the same page, reached through a query
  // string. Two entries for one surface is what made both of them render
  // active at once on the email-filtered inbox. Email is still first-class:
  // it is a medium filter, and Compose Email, inside this page.
  { menuKey: 'messages', label: 'Messages', route: '/messages' },
  { menuKey: 'phone', label: 'Phone', route: '/phone' },
  { menuKey: 'schedule', label: 'Schedule', route: '/schedule' },
  { menuKey: 'circles', label: 'Circles', route: '/circles' },
  { menuKey: 'referrals', label: 'Referrals', route: '/referrals' },
  { menuKey: 'team', label: 'Performance', route: '/team' },
  { menuKey: 'forms', label: 'Forms', route: '/forms' },
  { menuKey: 'files', label: 'Files', route: '/files' },
  { menuKey: 'projects', label: 'Projects', route: '/projects' },
  { menuKey: 'offerings', label: 'Offerings', route: '/offerings' },
  { menuKey: 'payments', label: 'Payments', route: '/payments' },
  { menuKey: 'businessProfile', label: 'Business Profile', route: '/business-profile' },
  { menuKey: 'portfolio', label: 'Portfolio', route: '/portfolio' },
  { menuKey: 'recruitment', label: 'Recruitment', route: '/recruitment' },
  { menuKey: 'marketplace', label: 'Marketplace', route: '/marketplace' },
  { menuKey: 'channels', label: 'Channels', route: '/channels' },
  { menuKey: 'settings', label: 'Settings', route: '/settings' },
];

export default function SideMenu() {
  const dispatch = useAppDispatch();
  const isSideMenuOpen = useAppSelector((state) => state.layout.isSideMenuOpen);
  const { logout } = useAuth();

  const currentCrmRole = useCurrentCrmRole();

  const visibleItems = SIDEBAR_ITEMS.filter((item) => {
    const allowed = MENU_KEY_ROLE_ACCESS[item.menuKey];
    if (allowed && !allowed.has(currentCrmRole.key)) return false;
    return true;
  });

  const handleLinkClick = () => {
    dispatch(setSideMenuOpen(false));
  };

  return (
    <>
      {/* Overlay */}
      {isSideMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => dispatch(setSideMenuOpen(false))}
        />
      )}

      {/* Side Menu */}
      <aside
        data-testid="side-menu"
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 transform transition-transform duration-300 ease-in-out ${
          isSideMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200">
          <span className="text-lg font-semibold text-gray-900">Menu</span>
          <button
            onClick={() => dispatch(setSideMenuOpen(false))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="overflow-y-auto h-[calc(100%-3.5rem-3rem)]" aria-label="Main navigation">
          <ul className="py-2">
            {visibleItems.map((item) => (
              <li key={item.menuKey}>
                <NavLink
                  to={item.route}
                  onClick={handleLinkClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-600 font-medium border-r-2 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`
                  }
                >
                  {getMenuIcon(item.menuKey, 'w-5 h-5')}
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer - Logout Button */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200">
          <button
            data-testid="side-menu-logout"
            onClick={() => {
              dispatch(setSideMenuOpen(false));
              logout();
            }}
            className="w-full h-12 px-4 flex items-center gap-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}
